import { Box, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { usePageContext } from '@providers/PageMetaProvider/model/PageContext';
import { DocumentType } from '@utils/enums.ts';
import { useGenerationProgress } from '@shared/hooks/useGenerationProgress';
import { StatusTag } from '@shared/components';
import { uploadWaitText } from '@shared/components/GenerationProgressDashboard/runState';
import styles from './index.module.scss';

interface DocumentStatusCellProps {
  status?: string;
  documentId: number | string;
  documentType: DocumentType;
  documentTypeName?: string;
  /** Why the generation failed. Shown on the row so the reason is not only
   *  in the server logs. Absent unless the document actually failed. */
  failureReason?: string;
}

const getDocumentTypeLabel = (type: DocumentType, nameOverride?: string) => {
  switch (type) {
    case DocumentType.DEMAND_LETTER:
      return 'Demand Letter';
    case DocumentType.DEPOSITION_SUMMARY:
      return 'Deposition Summary';
    case DocumentType.USE_CASE:
      return nameOverride || 'Document';
    default:
      return 'Document';
  }
};

function DocumentProgressTag({
  documentId,
  documentType,
  documentTypeName,
  progressData,
}: Omit<DocumentStatusCellProps, 'status'> & {
  progressData: ReturnType<typeof useGenerationProgress>;
}) {
  const { setValues } = usePageContext();
  const documentLabel = getDocumentTypeLabel(documentType, documentTypeName);

  const rawProgress = Math.round(progressData.overallProgress || 0);
  const progressValue = Math.min(
    100,
    Math.max(progressData.isGenerating ? 1 : 0, rawProgress)
  );
  // ZLAYT-1642: the percent does not move while a file waits for its upload
  // read; the row says so instead of a number that looks stuck.
  const waiting = !!uploadWaitText(progressData.tasks);

  const handleOpenProgress = () => {
    if (!setValues) return;

    const isDemand = documentType === DocumentType.DEMAND_LETTER;
    const isDepo = documentType === DocumentType.DEPOSITION_SUMMARY;
    const isUseCase = documentType === DocumentType.USE_CASE;

    setValues((prev) => ({
      ...prev,
      generateModalOpened: isDemand,
      generateDepositionSummaryModalOpened: isDepo,
      generateUseCaseModalOpened: isUseCase,
      generatingDocumentId: String(documentId),
      generatingDocumentType: documentLabel,
      progressModalAutoMinimize: false,
      progressModalExpandedAt: Date.now(),
    }));
  };

  return (
    <button
      type="button"
      className={styles.progressTag}
      onClick={handleOpenProgress}
      aria-label={`View ${documentLabel} progress`}
    >
      <span className={styles.label}>In progress</span>
      <span className={styles.percent}>
        {waiting ? 'Waiting' : `${progressValue}%`}
      </span>
      <span className={styles.bar}>
        <span
          className={styles.barFill}
          style={{ width: `${progressValue}%` }}
        />
      </span>
    </button>
  );
}

function DocumentStatusCell({
  status,
  documentId,
  documentType,
  documentTypeName,
  failureReason,
}: DocumentStatusCellProps) {
  const pipeline =
    documentType === DocumentType.DEPOSITION_SUMMARY
      ? 'deposition_summary'
      : documentType === DocumentType.USE_CASE
        ? 'use_case'
        : 'demand_letter';
  const normalizedStatus = String(status || '')
    .trim()
    .toLowerCase();
  const isInProgress =
    normalizedStatus === 'pending' || normalizedStatus === 'in progress';
  // A row that is still running asks the server for its progress. Without
  // it a reload, or a tab that missed the socket events, showed "In progress
  // 1%" until the run ended (ZLAYT-1514). Deposition summaries have no
  // progress route.
  const canPoll = isInProgress && pipeline !== 'deposition_summary';
  const progressData = useGenerationProgress(String(documentId), {
    enableSnapshot: canPoll,
    enablePolling: canPoll,
    enableSynthetic: false,
    pipeline,
  });

  if (!isInProgress || progressData.finalStatus) {
    const finalStatus =
      progressData.finalStatus === 'CANCELLED'
        ? 'Draft'
        : progressData.finalStatus?.toLowerCase();
    const resolvedStatus = finalStatus || status || '';
    const hasReason =
      resolvedStatus.trim().toLowerCase() === 'failed' && !!failureReason;

    // The tag keeps its own markup and is shared with two admin tables, so
    // the icon sits beside it rather than inside it.
    if (!hasReason) {
      return <StatusTag status={resolvedStatus} />;
    }

    return (
      <Box display="flex" alignItems="center" gap={0.5}>
        <StatusTag status={resolvedStatus} />
        <Tooltip
          title={failureReason}
          arrow
          slotProps={{
            tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 360 } },
          }}
        >
          <InfoOutlinedIcon
            aria-label="Why this failed"
            sx={{ fontSize: 16, color: '#B42318', cursor: 'help' }}
          />
        </Tooltip>
      </Box>
    );
  }

  return (
    <DocumentProgressTag
      documentId={documentId}
      documentType={documentType}
      documentTypeName={documentTypeName}
      progressData={progressData}
    />
  );
}

export default DocumentStatusCell;
